import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header, { HeaderLeft, HeaderRight } from '../../../layout/Header/Header';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import Icon from '../../../components/icon/Icon';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import Popovers from '../../../components/bootstrap/Popovers';
import Spinner from '../../../components/bootstrap/Spinner';
import useDarkMode from '../../../hooks/useDarkMode';
import LANG, { getLangWithKey, ILang } from '../../../lang';
import showNotification from '../../../components/extras/showNotification';
import AuthContext from '../../../contexts/authContext';
import { UserType } from '../../../common/data/dummyRolesData';
import OrganizationDropdown from '../../../components/Header/OrganizationDropdown';

// Map routes to section info (icon + translationKey)
// Order matters: more specific routes first!
const getSectionInfo = (pathname: string): { icon: string; translationKey: string } | null => {
	const path = pathname.replace(/^\//, ''); // Remove leading slash

	// ============================================
	// TALENT ROUTES
	// ============================================

	// Talent Settings
	if (path.startsWith('talent/settings/profile'))
		return { icon: 'Person', translationKey: 'Profile' };
	if (path.startsWith('talent/settings/subscription'))
		return { icon: 'CardMembership', translationKey: 'Subscription' };
	if (path.startsWith('talent/settings/billing'))
		return { icon: 'Receipt', translationKey: 'Billing Data' };

	// Talent Alliance
	if (path.startsWith('talent/alliance'))
		return { icon: 'GroupWork', translationKey: 'Alliance' };

	// Talent Projects (must check before generic /projects)
	if (path.startsWith('talent/projects') || path.startsWith('talent/work/projects'))
		return { icon: 'BusinessCenter', translationKey: 'Projects' };

	// Talent Milestones
	if (path.includes('talent') && path.includes('milestones'))
		return { icon: 'Flag', translationKey: 'Milestones' };

	// Talent Timesheets
	if (path.includes('talent') && path.includes('timesheets'))
		return { icon: 'Schedule', translationKey: 'Timesheets' };

	// Talent Quotes
	if (path.includes('talent') && path.includes('quotes'))
		return { icon: 'RequestQuote', translationKey: 'Quotes' };

	// Talent Finance / Invoices
	if (path.startsWith('talent/finance') || (path.includes('talent') && path.includes('invoices')))
		return { icon: 'Receipt', translationKey: 'Finance' };

	// Talent Chat
	if (path.startsWith('talent/chat')) return { icon: 'Chat', translationKey: 'Companies' };

	// Talent Peer Chat
	if (path.startsWith('talent/peer-chat')) return { icon: 'Forum', translationKey: 'Talents' };

	// Talent Growth
	if (path.startsWith('talent/peer-search'))
		return { icon: 'PersonSearch', translationKey: 'Find Talents' };
	if (path.startsWith('talent/opportunity-radar'))
		return { icon: 'TrackChanges', translationKey: 'Opportunity Radar' };
	if (path.startsWith('talent/insights')) return { icon: 'Insights', translationKey: 'Insights' };
	if (path.startsWith('talent/referral-credit'))
		return { icon: 'Share', translationKey: 'Referral Credit' };

	// Former Talent Messages redirect
	if (path.includes('talent') && path.includes('messages'))
		return { icon: 'Chat', translationKey: 'Messages' };

	// Talent Clients
	if (path.startsWith('talent/clients')) return { icon: 'Business', translationKey: 'Clients' };

	// ============================================
	// COMPANY ROUTES
	// ============================================

	// Company Settings (organization, team, subscription, legal settings)
	if (path.startsWith('company/settings') || path.startsWith('company/organizations'))
		return { icon: 'Settings', translationKey: 'Settings' };

	// Company Searches
	if (path.startsWith('company/searches')) return { icon: 'Search', translationKey: 'Searches' };

	// Company Insights
	if (path.startsWith('company/insights'))
		return { icon: 'Insights', translationKey: 'dashboard:companyInsights.title' };

	// Company Favorites
	if (path.startsWith('company/favorites'))
		return { icon: 'Favorite', translationKey: 'Favorites' };

	// Company My Talent
	if (path.startsWith('company/my-talent'))
		return { icon: 'Person', translationKey: 'My Talent' };

	// Company Quote Requests (HR section)
	if (path.startsWith('company/quote-requests') || path.startsWith('company/quotes/requests'))
		return { icon: 'RequestQuote', translationKey: 'Quote Requests' };

	// Company Chat
	if (path.startsWith('company/chat')) return { icon: 'Chat', translationKey: 'Chat' };

	// Company Trust Controls
	if (path.startsWith('company/trust'))
		return { icon: 'Shield', translationKey: 'Trust Controls' };
	if (path.startsWith('company/blacklist')) return { icon: 'Block', translationKey: 'Blacklist' };
	if (path.startsWith('company/team')) return { icon: 'Groups', translationKey: 'Team' };
	if (path.startsWith('company/subscription'))
		return { icon: 'CardMembership', translationKey: 'Subscription' };

	// Company Talent Collections
	if (path.startsWith('company/talent-collections'))
		return { icon: 'FolderSpecial', translationKey: 'Talent Collections' };

	// Company Talent Profile View
	if (path.startsWith('company/talents')) return { icon: 'Person', translationKey: 'Talents' };

	// Company Projects (check specific paths first)
	if (path.includes('company/projects') && path.includes('milestones'))
		return { icon: 'Flag', translationKey: 'Milestones' };
	if (path.includes('company/projects') && path.includes('timesheets'))
		return { icon: 'Schedule', translationKey: 'Timesheets' };
	if (path.startsWith('company/projects'))
		return { icon: 'BusinessCenter', translationKey: 'Projects' };

	// Company Quotes (Legal section)
	if (path.startsWith('company/quotes'))
		return { icon: 'RequestQuote', translationKey: 'Quotes' };

	// Company Contracts
	if (path.startsWith('company/contracts'))
		return { icon: 'Assignment', translationKey: 'Contracts' };

	// Company Invoices / Finance
	if (path.startsWith('company/invoices') || path.startsWith('company/finance'))
		return { icon: 'Receipt', translationKey: 'Invoices' };

	// Company HR (generic fallback)
	if (path.startsWith('company/hr')) return { icon: 'Groups', translationKey: 'HR' };

	// Company Legal (generic fallback)
	if (path.startsWith('company/legal')) return { icon: 'Gavel', translationKey: 'Legal' };

	// ============================================
	// GENERIC FALLBACKS
	// ============================================
	if (path.includes('/projects')) return { icon: 'BusinessCenter', translationKey: 'Projects' };
	if (path.includes('/milestones')) return { icon: 'Flag', translationKey: 'Milestones' };
	if (path.includes('/timesheets')) return { icon: 'Schedule', translationKey: 'Timesheets' };
	if (path.includes('/quotes')) return { icon: 'RequestQuote', translationKey: 'Quotes' };
	if (path.includes('/contracts')) return { icon: 'Assignment', translationKey: 'Contracts' };
	if (path.includes('/invoices')) return { icon: 'Receipt', translationKey: 'Invoices' };
	if (path.includes('/messages')) return { icon: 'Chat', translationKey: 'Messages' };

	// Default - no section info
	return null;
};

const SimpleHeader = () => {
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const { i18n, t } = useTranslation(['menu', 'settings']);
	const location = useLocation();
	const { userData } = useContext(AuthContext);
	const isCompany = userData?.role === UserType.COMPANY;

	const sectionInfo = getSectionInfo(location.pathname);

	const styledBtn: IButtonProps = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	/**
	 * Switches the application language and shows a confirmation notification.
	 * Same behaviour as in CommonHeaderRight and DashboardHeader: delegates to
	 * `i18n.changeLanguage()`, triggering a re-render of all translated
	 * components, and persists the choice via LanguageDetector (localStorage).
	 *
	 * @userstory US-I18N-02 — Switch language at any time without logging out
	 * @userstory US-I18N-03 — Preference is automatically saved by LanguageDetector
	 */
	const changeLanguage = (lng: ILang['key']['lng']) => {
		i18n.changeLanguage(lng).then(() => {
			showNotification(
				<span className='d-flex align-items-center'>
					<Icon icon={getLangWithKey(lng)?.icon} size='lg' className='me-1' />
					<span>
						{t('settings:languageChanged', { language: getLangWithKey(lng)?.text })}
					</span>
				</span>,
				t('settings:languageUpdated'),
			);
		});
	};

	return (
		<Header>
			<HeaderLeft>
				<div className='d-flex align-items-center w-100'>
					{sectionInfo && (
						<div className='d-flex align-items-center'>
							<Icon
								icon={sectionInfo.icon}
								size='2x'
								className='me-3'
								color='primary'
							/>
							<span
								className='fw-semibold text-primary'
								style={{ fontSize: '1.25rem' }}>
								{t(sectionInfo.translationKey)}
							</span>
						</div>
					)}

					{/* Organization Switcher for Company users */}
					{isCompany && (
						<div className='ms-auto d-none d-md-flex align-items-center'>
							<div className='bg-l10-primary rounded px-3 py-2'>
								<OrganizationDropdown />
							</div>
						</div>
					)}
				</div>
			</HeaderLeft>

			<HeaderRight>
				<div className='row g-3'>
					{/* Dark Mode */}
					<div className='col-auto'>
						<Popovers trigger='hover' desc='Dark / Light mode'>
							<Button
								{...styledBtn}
								onClick={() => setDarkModeStatus(!darkModeStatus)}
								className='btn-only-icon'
								aria-label='Toggle dark mode'>
								<Icon
									icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
									color={darkModeStatus ? 'info' : 'warning'}
									className='btn-icon'
								/>
							</Button>
						</Popovers>
					</div>

					{/* Lang Selector */}
					<div className='col-auto'>
						<Dropdown>
							<DropdownToggle hasIcon={false}>
								{typeof getLangWithKey(i18n.language as ILang['key']['lng'])
									?.icon === 'undefined' ? (
									<Button
										{...styledBtn}
										className='btn-only-icon'
										aria-label='Change language'>
										<Spinner isSmall inButton='onlyIcon' isGrow />
									</Button>
								) : (
									<Button
										{...styledBtn}
										icon={
											getLangWithKey(i18n.language as ILang['key']['lng'])
												?.icon
										}
										aria-label='Change language'
									/>
								)}
							</DropdownToggle>
							<DropdownMenu isAlignmentEnd>
								{Object.keys(LANG).map((i) => (
									<DropdownItem key={LANG[i].lng}>
										<Button
											icon={LANG[i].icon}
											onClick={() => changeLanguage(LANG[i].lng)}>
											{LANG[i].text}
										</Button>
									</DropdownItem>
								))}
							</DropdownMenu>
						</Dropdown>
					</div>
				</div>
			</HeaderRight>
		</Header>
	);
};

export default SimpleHeader;
